import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { PageHeader, GlassCard, PrimaryButton, GhostButton } from "@/components/Primitives";
import { inputCls, Field } from "@/components/Modal";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

const SECTIONS = [
  ["planning", "Planning"], ["design", "Design"], ["development", "Development"],
  ["content", "Content"], ["seo", "SEO"], ["testing", "Testing"],
];

const PROJECT_TYPES = ["Website", "Mobile App", "CRM", "CMS", "SEO", "Automation", "API Integration", "AI Solution", "Custom Project"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const STATUSES = ["active", "review", "deployed", "completed", "on_hold"];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      setForm({
        project_name: data.project_name || "",
        project_type: data.project_type || "Website",
        status: data.status || "active",
        priority: data.priority || "Medium",
        description: data.description || "",
        notes: data.notes || "",
        start_date: (data.start_date || "").slice(0, 10),
        target_completion_date: (data.target_completion_date || "").slice(0, 10),
        actual_completion_date: (data.actual_completion_date || "").slice(0, 10),
        estimated_value: data.estimated_value || 0,
        actual_revenue: data.actual_revenue || 0,
        progress: {
          planning: data.progress?.planning || 0,
          design: data.progress?.design || 0,
          development: data.progress?.development || 0,
          content: data.progress?.content || 0,
          seo: data.progress?.seo || 0,
          testing: data.progress?.testing || 0,
        },
      });
    } catch (e) {
      toast.error("Project not found");
      nav("/projects");
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const overall = useMemo(() => {
    if (!form) return 0;
    const v = Object.values(form.progress);
    return Math.round(v.reduce((s, n) => s + (Number(n) || 0), 0) / v.length);
  }, [form]);

  const confirm = useConfirm();

  if (!form) return <div className="text-white/40">Loading…</div>;

  const updateProgress = (k, value) => setForm({ ...form, progress: { ...form.progress, [k]: Math.max(0, Math.min(100, Number(value) || 0)) } });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: Number(form.estimated_value) || 0,
        actual_revenue: Number(form.actual_revenue) || 0,
        start_date: form.start_date || null,
        target_completion_date: form.target_completion_date || null,
        actual_completion_date: form.actual_completion_date || null,
      };
      await api.patch(`/projects/${id}`, payload);
      toast.success("Project saved");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Delete this project?",
      description: "All progress, attachments, and time tracking on this project will be lost. This cannot be undone.",
      confirmLabel: "Delete project",
    });
    if (!ok) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); nav("/projects"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="project-detail-page">
      <div className="mb-6">
        <Link to="/projects" className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1.5" data-testid="back-to-projects">
          <ArrowLeft className="w-3.5 h-3.5" /> All Projects
        </Link>
      </div>
      <PageHeader
        title={project.project_name}
        subtitle={`${project.client_name} · ${project.project_type}`}
        testId="project-detail-header"
        action={
          <div className="flex gap-3">
            <button onClick={remove} className="px-4 py-2.5 rounded-full bg-rose-400/10 hover:bg-rose-400/20 text-rose-300 text-sm border border-rose-400/20 inline-flex items-center gap-2" data-testid="delete-project-btn">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <PrimaryButton onClick={save} disabled={saving} data-testid="save-project-btn"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <GlassCard className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div className="font-display text-xl">Progress</div>
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Overall</div>
              <div className="font-display text-3xl text-[#007AFF]" data-testid="project-overall">{overall}%</div>
            </div>
          </div>
          <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden mb-8">
            <div className="h-full bg-gradient-to-r from-[#007AFF] to-[#5AC8FA] transition-all" style={{ width: `${overall}%` }} />
          </div>

          <div className="space-y-6">
            {SECTIONS.map(([k, label]) => (
              <div key={k} data-testid={`progress-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-white/70 font-medium">{label}</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="100"
                      value={form.progress[k]}
                      onChange={(e) => updateProgress(k, e.target.value)}
                      className="w-16 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-white text-sm text-right outline-none focus:border-[#007AFF]/50"
                      data-testid={`progress-input-${k}`}
                    />
                    <span className="text-xs text-white/40 w-3">%</span>
                  </div>
                </div>
                <input
                  type="range" min="0" max="100" value={form.progress[k]}
                  onChange={(e) => updateProgress(k, e.target.value)}
                  className="w-full accent-[#007AFF]"
                  data-testid={`progress-slider-${k}`}
                  style={{
                    background: `linear-gradient(to right, #007AFF 0%, #007AFF ${form.progress[k]}%, rgba(255,255,255,0.06) ${form.progress[k]}%, rgba(255,255,255,0.06) 100%)`,
                    height: 6, borderRadius: 999, appearance: "none", outline: "none",
                  }}
                />
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard>
          <div className="font-display text-xl mb-4">Details</div>
          <div className="space-y-4">
            <Field label="Project Name"><input className={inputCls} value={form.project_name} onChange={update("project_name")} data-testid="project-name-input" /></Field>
            <Field label="Type">
              <select className={inputCls} value={form.project_type} onChange={update("project_type")}>
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={update("status")}>
                  {STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <select className={inputCls} value={form.priority} onChange={update("priority")}>
                  {PRIORITIES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Estimated Value"><input type="number" className={inputCls} value={form.estimated_value} onChange={update("estimated_value")} /></Field>
              <Field label="Actual Revenue"><input type="number" className={inputCls} value={form.actual_revenue} onChange={update("actual_revenue")} /></Field>
            </div>
            <Field label="Start Date"><input type="date" className={inputCls} value={form.start_date} onChange={update("start_date")} /></Field>
            <Field label="Target Completion"><input type="date" className={inputCls} value={form.target_completion_date} onChange={update("target_completion_date")} /></Field>
            <Field label="Actual Completion"><input type="date" className={inputCls} value={form.actual_completion_date} onChange={update("actual_completion_date")} /></Field>
            <Field label="Description"><textarea rows={3} className={inputCls} value={form.description} onChange={update("description")} /></Field>
            <Field label="Notes"><textarea rows={3} className={inputCls} value={form.notes} onChange={update("notes")} /></Field>
          </div>
        </GlassCard>

        <GlassCard className="lg:col-span-3">
          <AttachmentsPanel parentType="project" parentId={id} testIdPrefix="project-att" />
        </GlassCard>
      </div>
    </div>
  );
}
